import $ from 'jquery';
import 'vue-template-compiler';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Swiper from 'swiper';
import Readmore from 'readmore-js';

import Section from '../Section';
import MediaWrapper from '../global/MediaWrapper';
import Dropdown from '../components/Dropdown.vue';
import { Breakpoints } from '../constants';

/** @typedef {import('theme-checker/dist/shopify').Variant} Variant */
/** @typedef {import('theme-checker/dist/shopify').Product} Product */
/** @typedef {import('theme-checker/dist/shopify').Media} Media */

const State = {
  ready: 'ready',
  soldOut: 'soldOut',
  added: 'added',
  unavailable: 'unavailable',
  adding: 'adding',
};

const MOBILE_BREAKPOINT = Breakpoints.xs;

@Component({
  components: {
    'wetheme-dropdown': Dropdown,
  },
})
class ProductForm extends Vue {

  /** @type {number | null} */
  @Prop({required: true})
  initialVariantId;

  /** @type {import('theme-checker/dist/shopify').Product} */
  @Prop({required: true})
  product;

  /** @type {import('../ThemeBase').default} */
  @Prop({required: true})
  theme;

  /** @type {Product} */
  @Prop({required: true})
  productSection;

  /** @type {boolean} */
  @Prop({required: true})
  isFeaturedProduct;

  /** @type {boolean} */
  @Prop({required: true})
  thumbnailChangesVariant;

  /** @type {boolean} */
  @Prop({required: true})
  hasThumbnails;

  /** @type {boolean} */
  @Prop({required: true})
  imageZoomEnabled;

  /** @type {boolean} */
  @Prop({required: true})
  videoLoopEnabled;

  /** @type {string} */
  @Prop({required: true})
  sectionId;

  /** @type {'drawer' | 'added' | 'page'} */
  @Prop({required: true})
  cartMethod;

  /** @type {number} */
  @Prop({required: true})
  slidesPerView;

  /** @type {string} */
  option1 = '';
  /** @type {string | null} */
  option2 = null;
  /** @type {string | null} */
  option3 = null;
  /** @type {boolean} */
  addingToCart;
  /** @type {boolean} */
  afterAddToCart;
  /** @type {string} */
  mediaWrapperSpacing;
  /** @type {number} */
  activeMediaId;
  /** @type {Map<number, MediaWrapper>} */
  mediaWrapperById;
  /** @type {boolean} */
  showOutOfStockForm;
  /** @type {boolean} */
  isMobile;
  /** @type {number} */
  quantity = 1;

  /** @type {Swiper} */
  mediaSlider = null;

  data() {
    const variant = this.product.variants.find(v => v.id === this.initialVariantId);
    let media;
    if (variant.featured_media) {
      media = variant.featured_media;
    } else if (this.product.media && this.product.media.length > 0) {
      media = this.product.media[0];
    }
    return {
      option1: variant.option1,
      option2: variant.option2,
      option3: variant.option3,
      addingToCart: false,
      afterAddToCart: false,
      mediaWrapperSpacing: '',
      activeMediaId: media ? media.id : undefined,
      mediaWrapperById: new Map(),
      showOutOfStockForm: false,
      isMobile: window.matchMedia(`only screen and (max-width: ${MOBILE_BREAKPOINT.max}px)`).matches,
      quantity: 1,
    };
  }

  /** @returns {Variant | null} */
  get variant() {
    return this.product.variants.find(v => v.option1 === this.option1 && v.option2 == this.option2 && v.option3 === this.option3);
  }

  get isSale() {
    return this.variant && this.variant.compare_at_price && this.variant.compare_at_price > this.variant.price;
  }

  /** @return {State[keyof State]} */
  get state() {
    if (this.addingToCart) {
      return State.adding;
    }
    if (this.afterAddToCart) {
      return State.added;
    }
    if (this.variant) {
      if (this.variant.available) {
        return State.ready;
      } else {
        return State.soldOut;
      }
    } else {
      return State.unavailable;
    }
  }

  /** @returns {boolean} */
  get addToCartDisabled() {
    return !this.variant || !this.variant.available;
  }

  /** @returns {Media | undefined} */
  get activeMedia() {
    return (this.product.media || []).find(m => m.id === this.activeMediaId);
  }

  /** @returns {Media | undefined} */
  get firstOrActiveModel() {
    if (this.activeMedia && this.activeMedia.media_type === 'model') {
      return this.activeMedia;
    }
    return (this.product.media || []).find(m => m.media_type === 'model');
  }

  increaseQuantity() {
    this.quantity += 1;
  }

  decreaseQuantity() {
    this.quantity = Math.max(1, this.quantity - 1);
  }

  /** @param {number} mediaId */
  getMedia(mediaId) {
    let media = this.mediaWrapperById.get(mediaId);
    if (!media) {
      const m = (this.product.media || []).find(m => m.id === mediaId);
      const target = this.$refs.mediaTargets[m.position - 1];
      media = new MediaWrapper(this.theme, this.sectionId, m, target);
      target.addEventListener('media-play', this.onMediaPlay.bind(this));
      target.addEventListener('media-pause', this.onMediaPause.bind(this));
      target.addEventListener('media-load', this.onMediaLoaded.bind(this));
      this.mediaWrapperById.set(mediaId, media);
    }
    return media;
  }

  loadSlider() {
    this.mediaSlider = new Swiper(this.$el.querySelector('.swiper-container'), {
      direction: 'horizontal',
      slidesPerView: this.slidesPerView,
      centeredSlides: this.slidesPerView > 1,
      updateOnImagesReady: true,
      spaceBetween: 20,
      threshold: 10,
      autoHeight: true,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
    });
    this.mediaSlider.on('slideChange', () => {
      if (this.thumbnailChangesVariant) {
        const media = this.product.media[this.mediaSlider.activeIndex];
        this.changeDropdownsToMedia(media);
      }
      this.mediaWrapperById.forEach(m => m.pause());
    });
    this.mediaSlider.allowTouchMove = this.isFeaturedProduct || this.isMobile;
    if (this.variant.featured_media) {
      this.slideTo(this.getMedia(this.variant.featured_media.id), true);
    }
  }

  unloadSlider() {
    console.log('destroy');
    this.mediaSlider.destroy();
    this.mediaSlider = undefined;
  }

  get hasSlider() {
    return this.isFeaturedProduct || this.isMobile;
  }

  get showThumbails() {
    return !this.isFeaturedProduct && this.hasThumbnails && !this.isMobile;
  }

  mounted() {
    this.$el.querySelector('#AddToCartForm').addEventListener('submit', this.onAddToCart);

    this.mediaWrapperById = new Map();

    if (this.showThumbails || this.hasSlider) {
      this.loadSlider();
    }

    this.$el.addEventListener('product-media-select', this.onMediaSelect.bind(this));

    if (!this.hasThumbnails) {
      // Load zoom effect for full-size images when enabled
      $(this.$el).find('[data-image-zoom-enable="true"]').addClass('product-single__media--selected');
    }

    if (this.firstOrActiveModel) {
      // We have at least one model => load XR library so
      // it can show/hide the button based on browser/device support
      window.Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
        },
      ]);
      document.addEventListener('shopify_xr_launch', () => {
        // The launch of XR viewer triggers beforeload event, we need to reappear page content
        setTimeout(() => {
          document.body.classList.remove('unloading');
        }, 100);
      });
    }

    window.ShopifyXR = window.ShopifyXR || function() {
      (window.ShopifyXR.q = window.ShopifyXR.q || []).push(arguments);
    }
    const models = (this.product.media || []).filter(m => m.media_type === 'model');
    if (window.ShopifyXR.addModels) {
      window.ShopifyXR.addModels(models);
    } else {
      window.ShopifyXR('addModels', models);
    }
  }

  @Watch('variant')
  onVariantChange() {
    if (this.variant) {
      if (!this.variant.available) {
        // Wait for user to show it
        this.showOutOfStockForm = false;
      }
      this.setVariantUrl(this.variant);
      this.showPaymentButtons();
      if (this.variant.featured_media) {
        const mediaElement = this.$el.querySelector(`[data-media-id="${this.variant.featured_media.id}"]`);
        this.onMediaElementSelect(mediaElement, { load: true });
      }
    } else {
      this.hidePaymentButtons();
    }
  }

  onShowOutOfStockForm(e) {
    e.preventDefault();
    // When the HERE button is clicked, the target is whole text
    if (e.currentTarget.contains(e.target.closest('a'))) {
      this.showOutOfStockForm = true;
    }
  }

  onMediaPlay(e) {
    if (this.mediaSlider) {
      this.mediaSlider.allowTouchMove = false;
    }
  }

  onMediaPause(e) {
    if (this.mediaSlider) {
      this.mediaSlider.allowTouchMove = this.isFeaturedProduct || this.isMobile;
    }
  }

  onMediaLoaded(e) {
    e.detail.parent.querySelector('.product-medias__media').addEventListener('click', this.onMediaActivate.bind(this));
  }

  onMediaSelect(e) {
    const element = /** @type {HTMLElement} */ ($(this.$el).find('.product-single__media')[e.detail.index]);
    const mediaWrapper = this.mediaWrapperById.get(parseInt(element.dataset.mediaId, 10));
    const isVideo = mediaWrapper.media.media_type === 'video' || mediaWrapper.media.media_type === 'external_video';
    const isQuickShop = this.$el.closest('#ShopNowContainer');
    this.onMediaElementSelect(element, {
      load: e.detail.forceLoad,
      forcePlay: e.detail.forcePlay || (!this.isMobile && !isQuickShop && isVideo), // Autoplay video on non-mobile and not in quick shop
      focus: e.detail.focus,
    });
  }

  /** @param {KeyboardEvent} e */
  onMediaActivate(e) {
    const target = /** @type HTMLElement */ (e.target);
    const currentTarget = /** @type HTMLElement */ (e.currentTarget);
    if (e.type === 'keydown') {
      if (e.keyCode !== 13 && e.keyCode !== 27) {
        return;
      }
      if (target.classList.contains('shopify-model-viewer-ui__button') || target.classList.contains('plyr__controls__item')) {
        return;
      }
    }
    e.preventDefault();

    if (target.classList.contains('product-medias__view-in-space')) {
      // Don't do anything on "view in your space" click, it's handled by Shopify XR library
      return;
    }

    if (target.tagName === 'MODEL-VIEWER' || target.classList.contains('shopify-model-viewer-ui__controls-overlay')) {
      // Don't do anything on 3d model overlay click, it will trigger an event itself
      return;
    }

    const isLoaded = currentTarget.classList.contains('product-single__media--loaded');
    const isActive = currentTarget.classList.contains('product-single__media--active')

    if (this.hasThumbnails) {
      this.onThumbnailClick(currentTarget);
    }

    if (isLoaded && isActive) {
      // Loaded and active media will handle click internally in the component itself
      return;
    }

    this.onMediaElementSelect(currentTarget, { load: true, togglePlay: true });
  }

  onMediaElementSelect(element, options) {
    if (!element) {
      return;
    }
    const mediaWrapper = this.getMedia(parseInt(element.dataset.mediaId, 10));
    this.activeMediaId = mediaWrapper.media.id;
    this.slideTo(mediaWrapper);
    // Pause all others
    this.mediaWrapperById.forEach((otherMediaWrapper) => {
      if (otherMediaWrapper.mediaId !== mediaWrapper.mediaId) {
        otherMediaWrapper.pause();
      }
    });
    $(this.$el).find('.product-single__media--selected').not(element).removeClass('product-single__media--selected');

    element.classList.add('product-single__media--selected')


    if (options.togglePlay) {
      mediaWrapper.togglePlay();
    } else if (options.forcePlay) {
      mediaWrapper.play();
    } else if (options.load) {
      mediaWrapper.load();
    }
    if (options.focus) {
      mediaWrapper.focus();
    }
  }

  async onAddToCart(e) {
    e.preventDefault();
    this.addingToCart = true;
    try {
      const data = $(this.$el.querySelector('#AddToCartForm')).serialize();
      const response = await window.fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data,
      });
      if (!response.ok) {
        // FIXME: error handling
        return;
      }
      const response2 = await window.fetch('/cart.js');
      if (!response2.ok) {
        // FIXME: error handling
        return;
      }
      const cart = await response2.json();

      //await response.json();
      this.addingToCart = false;
      if (this.cartMethod === 'drawer') {
        this.theme.toggleRightDrawer('cart', true, { cart: cart })
      } else if (this.cartMethod === 'added') {
        this.afterAddToCart = true;
        setTimeout(() => {
          this.afterAddToCart = false;
        }, 2000);
        this.theme.updateCartDrawer(cart);
      } else if (this.cartMethod === 'page') {
        window.location.href = '/cart';
      }
    } catch (e) {
      console.error('Unable to add to cart: ', e);
      // FIXME error handling
    }

  }

  onThumbnailClick(target) {
    const mediaWrapper = this.getMedia(parseInt(target.dataset.mediaId, 10));
    this.slideTo(mediaWrapper);

    this.changeDropdownsToMedia(mediaWrapper.media);
  }

  slideTo(mediaWrapper, skipAnimation = false) {
    if (this.mediaSlider) {
      this.mediaSlider.slideTo(mediaWrapper.media.position - 1, this.hasSlider && !skipAnimation ? 200 : 0);
    }
  }

  changeDropdownsToMedia(media) {
    // select variant
    if (this.thumbnailChangesVariant) {
      const variant = this.product.variants.find(v => v.featured_media && v.featured_media.id === media.id);
      if (variant) {
        this.option1 = variant.option1;
        this.option2 = variant.option2;
        this.option3 = variant.option3;
      }
    }
  }

  setVariantOption(value, name) {
    this[`option${name}`] = value;
  }

  /** @param {Variant} variant */
  setVariantUrl(variant) {
    const hasMoreVariants = this.product.variants.length > 1;
    if (history.replaceState && !this.isFeaturedProduct && hasMoreVariants) {
      // Change url to include variant if we're not on homepage
      let search = [];
      if (window.location.search.length > 1) {
        // select non-variant query params
        search = window.location.search.slice(1).split('&').filter((q) => {
          return q.indexOf('variant=') !== 0;
        });
      }
      // add current variant
      search.push('variant=' + variant.id);

      // set the url to include the variant
      const newurl = window.location.protocol + '//' + window.location.host + window.location.pathname + '?' + search.join('&');
      window.history.replaceState({ path: newurl }, '', newurl);
    }
  }

  hidePaymentButtons = () => {
    $('.shopify-payment-button').hide();
    $("#ShopNowContainer #AddToCart").addClass('full-width-button');
  };

  showPaymentButtons = () => {
    $('.shopify-payment-button').show();
    $("#ShopNowContainer #AddToCart").removeClass('full-width-button');
  };

  /** @returns {string} */
  money(price) {
    return this.theme.formatMoney(price);
  }

  onWindowResizeBreakpoint({ breakpoint, oldBreakpoint }) {
    const newIsMobile = breakpoint.order <= MOBILE_BREAKPOINT.order;
    const oldWasMobile = oldBreakpoint.order <= MOBILE_BREAKPOINT.order;
    if (newIsMobile !== oldWasMobile) {
      this.isMobile = newIsMobile;
      if (this.mediaSlider) {
        this.mediaSlider.allowTouchMove = this.hasSlider;
      }

      if (!this.hasThumbnails) {
        if (this.hasSlider && !this.mediaSlider) {
          this.loadSlider();
        } else if (!this.hasSlider && this.mediaSlider) {
          this.unloadSlider();
        }
      }
    }
  }
}

export default class ProductSection extends Section {
  constructor(theme, element, pageLoad, options = {}) {
    super(theme, element, pageLoad);

    var product_json_el = this.element.querySelector('.product-json');
    if (!product_json_el) {
      // No product on the page - maybe just product placeholder
      return;
    }
    this.product = JSON.parse(product_json_el.textContent);

    this.vue = new Vue({
      el: this.element.querySelector('.product-form-vue'),
      components: {
        'wetheme-product-form': ProductForm,
      },
      data: () => ({
        productSection: this,
        product: this.product,
        theme: this.theme,
        isFeaturedProduct: this.isFeaturedProduct,
        slidesPerView: options ? options.slidesPerView || 1 : 1,
      }),
    });

    this.theme.loadReviews({ products: true, badges: true });

    this.fixSticky();
    this.loadReadmore();
    this.loadTabs();
    this.scrollToPostedForm();
  }

  fixSticky = () => {
    const productSticky = /** @type {HTMLElement?} */ (this.element.querySelector('.product-sticky'));
    const headerSticky = document.querySelector('.sticky-header');

    if (productSticky && headerSticky) {
      productSticky.style.top = `${headerSticky.clientHeight}px`;
    }
  }

  get isFeaturedProduct() {
    return false;
  }

  onWindowResize = () => {
    this.fixSticky();
    this.loadReadmore();
  }

  onWindowResizeBreakpoint = ({ breakpoint, oldBreakpoint }) => {
    (/** @type {ProductForm} */ (this.vue.$children[0])).onWindowResizeBreakpoint({ breakpoint, oldBreakpoint });
  }


  loadReadmore = () => {
    const readMoreEl = this.element.querySelector('.product-description.readmore');
    if (!readMoreEl) {
      return;
    }

    let thisHeight = 100;
    if (this.element.querySelector('.product-description_full-width')) {
      thisHeight = 150;
      if (this.theme.getWindowWidth() <= 768) {
        thisHeight = 100;
      }
    }

    if (!readMoreEl.classList.contains('rmjs-1')) {
      this.readMore = new Readmore(readMoreEl, {
        speed: 100,
        collapsedHeight: thisHeight,
        moreLink: `<div class="read-more-link"><a href="#">${this.theme.translations.read_more_html} <i class="fa fa-angle-down" aria-hidden="true"></i></a></div>`,
        lessLink: `<div class="read-less-link"><a href="#">${this.theme.translations.read_less_html} <i class="fa fa-angle-up" aria-hidden="true"></i></a></div>`,
        beforeToggle: function(trigger, element, expanded) {
          if (expanded) {
            const headerEl = document.querySelector('.sticky-header');
            const stickyHeaderHeight = headerEl ? headerEl.clientHeight : 0;
            $('html, body').animate({
              scrollTop: $(element).offset().top - stickyHeaderHeight,
            }, 100);
          }
          $(element).toggleClass('closed');
        }
      });
    }
  }

  loadTabs = () => {
    $(this.element).find('ul.tabs').each(function(){
      var active, content, links = $(this).find('a');
      active = links.first().addClass('active');
      content = $(active.attr('href'));
      links.not(':first').each(function () {
        $($(this).attr('href')).hide();
      });
      $(this).find('a').click(function(e){
        active.removeClass('active');
        content.hide();
        active = $(this);
        content = $($(this).attr('href'));
        active.addClass('active');
        content.fadeIn();
        return false;
      });
    });
  }

  scrollToPostedForm = () => {
    if($('.out-of-stock-form .posted-successfully').length) {
      $('html, body').animate({
        scrollTop: $('.form-vertical').offset().top,
      }, 100);
    }
  }
}
